import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Brain, MonitorSmartphone, Palette, TrendingUp, Clapperboard } from 'lucide-react';
import { siteData } from '../data/mock';

const iconMap = [Brain, MonitorSmartphone, Palette, TrendingUp, Clapperboard];

function TiltCard({ children, index }) {
  const cardRef = useRef(null);
  const [transform, setTransform] = useState('');

  const onMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTransform(`perspective(1000px) rotateX(${-y * 8}deg) rotateY(${x * 8}deg) translateZ(0)`);
  };
  const onLeave = () => setTransform('perspective(1000px) rotateX(0) rotateY(0) translateZ(0)');

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, delay: index * 0.08 }}
      style={{ transform }}
      className="tilt-card glass rounded-3xl p-8 md:p-10 relative overflow-hidden group cursor-pointer"
    >
      {children}
    </motion.div>
  );
}

export default function Services() {
  const { services } = siteData;

  return (
    <section id="services" className="section bg-nebula relative overflow-hidden">
      <div className="absolute inset-0 grid-lines opacity-15 pointer-events-none" />
      <div className="container relative z-10">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-8">
          <div>
            <div className="text-xs tracking-[0.4em] uppercase text-[#d5b56b] mb-6">
              — {services.label}
            </div>
            <h2 className="font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#eef1f8]">
              A full-stack
              <br />
              <span className="italic text-gold">marketing partner</span>
            </h2>
          </div>
          <p className="max-w-md text-[#eef1f8]/60 text-base leading-relaxed">
            {services.description}
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.items.map((s, i) => {
            const Icon = iconMap[i] || Brain;
            const isFeature = i === services.items.length - 1 && services.items.length % 2 === 1;
            return (
              <div key={s.title} className={isFeature ? 'md:col-span-2' : ''}>
                <TiltCard index={i}>
                  <div className="tilt-inner">
                    <div className="flex items-start justify-between mb-8">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#d5b56b] to-[#8a6a2a] grid place-items-center shadow-lg shadow-[#d5b56b]/20">
                        <Icon size={26} className="text-[#0a1230]" />
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="font-serif text-[#d5b56b]/60 text-lg">{s.tag}</span>
                        <div className="w-11 h-11 rounded-full border border-[#d5b56b]/30 grid place-items-center group-hover:bg-[#d5b56b] group-hover:border-[#d5b56b] transition-all duration-500">
                          <ArrowUpRight size={18} className="text-[#d5b56b] group-hover:text-[#0a1230] transition-colors" />
                        </div>
                      </div>
                    </div>
                    <h3 className="font-serif text-3xl md:text-4xl text-[#eef1f8] mb-4 leading-tight">
                      {s.title}
                    </h3>
                    <p className={`text-sm text-[#eef1f8]/60 leading-relaxed ${isFeature ? 'max-w-2xl' : 'max-w-md'}`}>
                      {s.desc}
                    </p>
                  </div>
                  {/* Glow */}
                  <div className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-[#d5b56b]/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
                </TiltCard>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
